<template>
  <div class="google-data-page">
    <div class="google-data-page__header">
      <div>
        <div class="google-data-page__title">{{ pageTitle }}</div>
        <div class="google-data-page__desc">{{ pageDesc }}</div>
      </div>
      <div class="google-data-page__header-actions">
        <div v-if="showRangePicker" class="google-data-page__range">
          <el-radio-group
            v-model="rangeParams.time_range"
            size="small"
            @change="handleRangeChange"
          >
            <el-radio-button
              v-for="item in timeRangeOptions"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-radio-button>
          </el-radio-group>
          <el-date-picker
            v-if="rangeParams.time_range === 'custom'"
            v-model="rangeParams.custom_time"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            size="small"
            @change="refreshCurrentTab"
          ></el-date-picker>
        </div>
        <el-date-picker
          v-if="showMonthPicker"
          v-model="month"
          type="month"
          value-format="yyyy-MM"
          placeholder="选择月份"
          size="small"
          @change="refreshCurrentTab"
        ></el-date-picker>
        <el-button
          size="small"
          icon="el-icon-refresh"
          @click="refreshCurrentTab"
        >
          刷新
        </el-button>
      </div>
    </div>

    <div class="google-data-page__content">
      <section v-if="activeTab === 'overview'">
        <div v-loading="overviewLoading">
          <div class="google-data-page__stats google-data-page__stats--wide">
            <stat-card
              label="已绑定站点"
              :value="overview.enabled_site_count"
              icon="el-icon-connection"
              tone="blue"
            />
            <stat-card
              label="GA4访问数"
              :value="formatNumber(overview.sessions)"
              :extra="formatMom(overview.mom && overview.mom.sessions)"
              :extra-class="momClass(overview.mom && overview.mom.sessions)"
              icon="el-icon-data-line"
              tone="green"
            />
            <stat-card
              label="GSC点击"
              :value="formatNumber(overview.search_clicks)"
              :extra="formatMom(overview.mom && overview.mom.search_clicks)"
              :extra-class="
                momClass(overview.mom && overview.mom.search_clicks)
              "
              icon="el-icon-position"
              tone="purple"
            />
            <stat-card
              label="GSC曝光"
              :value="formatNumber(overview.search_impressions)"
              :extra="
                formatMom(overview.mom && overview.mom.search_impressions)
              "
              :extra-class="
                momClass(overview.mom && overview.mom.search_impressions)
              "
              icon="el-icon-view"
              tone="orange"
            />
            <stat-card
              label="同步异常"
              :value="overview.failed_sync_count"
              icon="el-icon-warning-outline"
              tone="red"
            />
            <stat-card
              label="系统站点"
              :value="overview.site_count"
              icon="el-icon-s-grid"
              tone="gray"
            />
          </div>
          <div class="google-data-page__freshness">
            数据更新时间：{{ formatTime(overview.last_sync_at) }}，对比范围：{{
              overview.previous_month || "-"
            }}
          </div>

          <div class="google-data-page__dashboard-grid">
            <div class="google-data-page__panel google-data-page__trend-panel">
              <div class="google-data-page__panel-head">
                <div class="google-data-page__panel-title">趋势对比</div>
                <span class="google-data-page__panel-note">
                  {{ currentRangeLabel }}
                </span>
              </div>
              <google-trend-chart :rows="overviewTrendRows" :height="230" />
            </div>

            <div class="google-data-page__panel">
              <div class="google-data-page__panel-head">
                <div class="google-data-page__panel-title">异常站点</div>
                <el-button type="text" @click="$router.push('/google/logs')">
                  查看全部
                </el-button>
              </div>
              <el-table
                :data="overviewFailedLogs"
                size="small"
                empty-text="暂无同步异常"
              >
                <el-table-column
                  prop="site_name"
                  label="站点"
                  min-width="120"
                  show-overflow-tooltip
                />
                <el-table-column
                  prop="error_message"
                  label="异常原因"
                  min-width="150"
                  show-overflow-tooltip
                />
                <el-table-column label="时间" width="135">
                  <template v-slot="scope">
                    {{
                      formatTime(
                        scope.row.last_sync_at || scope.row.create_time
                      )
                    }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>

          <div
            class="google-data-page__panel google-data-page__overview-queries"
          >
            <div class="google-data-page__panel-head">
              <div class="google-data-page__panel-title">
                热门搜索词（Queries）
              </div>
              <el-button type="text" @click="$router.push('/google/data')">
                查看站点详情
              </el-button>
            </div>
            <el-table
              v-loading="overviewQueriesLoading"
              :data="overviewTopQueries"
              size="small"
              empty-text="暂无搜索词数据"
            >
              <el-table-column label="序号" width="58">
                <template v-slot="scope">{{ scope.$index + 1 }}</template>
              </el-table-column>
              <el-table-column
                prop="query_text"
                label="搜索词"
                min-width="220"
                show-overflow-tooltip
              />
              <el-table-column prop="search_clicks" label="点击" width="90" />
              <el-table-column
                prop="search_impressions"
                label="曝光"
                width="100"
              />
              <el-table-column prop="search_ctr" label="CTR" width="90" />
              <el-table-column
                prop="search_position"
                label="平均排名"
                width="100"
              />
            </el-table>
          </div>

          <div class="google-data-page__panel">
            <div class="google-data-page__panel-head">
              <div class="google-data-page__panel-title">
                Top 站点（按 GA4 访问数）
              </div>
              <el-button type="text" @click="$router.push('/google/data')">
                查看全部站点数据
              </el-button>
            </div>
            <div class="data-table-scroll" style="--table-min-width: 1190px">
              <el-table
                v-loading="overviewSiteLoading"
                :data="overviewSites"
                empty-text="暂无站点数据"
              >
                <el-table-column label="排名" width="70">
                  <template v-slot="scope">{{ scope.$index + 1 }}</template>
                </el-table-column>
                <el-table-column label="站点 / 域名" min-width="270">
                  <template v-slot="scope">
                    <div class="google-data-page__site-cell">
                      <div
                        class="google-data-page__site-name"
                        :title="scope.row.site_name"
                      >
                        {{ scope.row.site_name }}
                      </div>
                      <div
                        class="google-data-page__site-url"
                        :title="scope.row.site_url"
                      >
                        {{ scope.row.site_url }}
                      </div>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="访问数" width="130">
                  <template v-slot="scope">
                    <div class="google-data-page__table-metric">
                      <span>{{ formatNumber(scope.row.sessions) }}</span>
                      <span
                        :class="
                          momClass(scope.row.mom && scope.row.mom.sessions)
                        "
                      >
                        {{ formatMom(scope.row.mom && scope.row.mom.sessions) }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="访客数" width="130">
                  <template v-slot="scope">
                    <div class="google-data-page__table-metric">
                      <span>{{ formatNumber(scope.row.active_users) }}</span>
                      <span
                        :class="
                          momClass(scope.row.mom && scope.row.mom.active_users)
                        "
                      >
                        {{
                          formatMom(scope.row.mom && scope.row.mom.active_users)
                        }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="GSC点击" width="130">
                  <template v-slot="scope">
                    <div class="google-data-page__table-metric">
                      <span>{{ formatNumber(scope.row.search_clicks) }}</span>
                      <span
                        :class="
                          momClass(scope.row.mom && scope.row.mom.search_clicks)
                        "
                      >
                        {{
                          formatMom(
                            scope.row.mom && scope.row.mom.search_clicks
                          )
                        }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="GSC曝光" width="130">
                  <template v-slot="scope">
                    <div class="google-data-page__table-metric">
                      <span>{{
                        formatNumber(scope.row.search_impressions)
                      }}</span>
                      <span
                        :class="
                          momClass(
                            scope.row.mom && scope.row.mom.search_impressions
                          )
                        "
                      >
                        {{
                          formatMom(
                            scope.row.mom && scope.row.mom.search_impressions
                          )
                        }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="CTR" width="130">
                  <template v-slot="scope">
                    <div class="google-data-page__table-metric">
                      <span>{{ scope.row.search_ctr }}</span>
                      <span :class="momClass(scope.row.search_ctr_mom)">
                        {{ formatPointMom(scope.row.search_ctr_mom) }}
                      </span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column label="同步状态" width="110">
                  <template v-slot="scope">
                    <status-badge
                      :status="scope.row.last_sync_status"
                    ></status-badge>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="90">
                  <template v-slot="scope">
                    <el-button type="text" @click="openSiteDetail(scope.row)">
                      详情
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'site-data'">
        <div v-loading="siteDataLoading">
          <div class="google-data-page__site-main">
            <div class="google-data-page__panel google-data-page__filter-panel">
              <div class="google-data-page__filters">
                <el-input
                  v-model="siteParams.keyword"
                  clearable
                  placeholder="请输入站点名称或域名"
                  size="small"
                  class="google-data-page__keyword"
                  @keyup.enter.native="getSiteData"
                ></el-input>
                <el-select
                  v-model="siteParams.site_id"
                  filterable
                  clearable
                  placeholder="全部站点"
                  size="small"
                  class="google-data-page__site-select"
                  @change="getSiteData"
                >
                  <el-option
                    v-for="item in siteOptions"
                    :key="item.id"
                    :label="`${item.name}（${item.url}）`"
                    :value="item.id"
                  ></el-option>
                </el-select>
                <el-button
                  size="small"
                  type="primary"
                  icon="el-icon-search"
                  @click="getSiteData"
                >
                  查询
                </el-button>
                <el-button size="small" @click="resetSiteFilters"
                  >重置</el-button
                >
              </div>
            </div>

            <div class="google-data-page__stats google-data-page__stats--site">
              <stat-card
                label="已绑定站点总数"
                :value="siteSummary.site_count"
                icon="el-icon-monitor"
                tone="blue"
              />
              <stat-card
                label="访问数"
                :value="formatNumber(siteSummary.sessions)"
                :extra="formatMom(siteSummary.mom && siteSummary.mom.sessions)"
                :extra-class="
                  momClass(siteSummary.mom && siteSummary.mom.sessions)
                "
                icon="el-icon-data-line"
                tone="green"
              />
              <stat-card
                label="GSC点击"
                :value="formatNumber(siteSummary.search_clicks)"
                :extra="
                  formatMom(siteSummary.mom && siteSummary.mom.search_clicks)
                "
                :extra-class="
                  momClass(siteSummary.mom && siteSummary.mom.search_clicks)
                "
                icon="el-icon-position"
                tone="purple"
              />
              <stat-card
                label="同步失败"
                :value="overview.failed_sync_count || 0"
                icon="el-icon-warning-outline"
                tone="red"
              />
            </div>

            <div class="google-data-page__panel">
              <div class="google-data-page__panel-head">
                <div class="google-data-page__panel-title">站点数据列表</div>
                <el-button
                  v-if="$hasPermission('google_data', 'sync')"
                  size="small"
                  type="primary"
                  icon="el-icon-refresh"
                  :disabled="!siteParams.site_id"
                  @click="syncSelectedSite"
                >
                  同步选中站点
                </el-button>
              </div>
              <div class="data-table-scroll" style="--table-min-width: 1150px">
                <el-table :data="sitePager.list" empty-text="暂无站点数据">
                  <el-table-column label="站点 / 域名" min-width="260">
                    <template v-slot="scope">
                      <div class="google-data-page__site-cell">
                        <div
                          class="google-data-page__site-name"
                          :title="scope.row.site_name"
                        >
                          {{ scope.row.site_name }}
                        </div>
                        <div
                          class="google-data-page__site-url"
                          :title="scope.row.site_url"
                        >
                          {{ scope.row.site_url }}
                        </div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="GA4访问数" width="130">
                    <template v-slot="scope">
                      <div class="google-data-page__table-metric">
                        <span>{{ formatNumber(scope.row.sessions) }}</span>
                        <span
                          :class="
                            momClass(scope.row.mom && scope.row.mom.sessions)
                          "
                        >
                          {{
                            formatMom(scope.row.mom && scope.row.mom.sessions)
                          }}
                        </span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="GSC点击" width="125">
                    <template v-slot="scope">
                      <div class="google-data-page__table-metric">
                        <span>{{ formatNumber(scope.row.search_clicks) }}</span>
                        <span
                          :class="
                            momClass(
                              scope.row.mom && scope.row.mom.search_clicks
                            )
                          "
                        >
                          {{
                            formatMom(
                              scope.row.mom && scope.row.mom.search_clicks
                            )
                          }}
                        </span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="GSC曝光" width="130">
                    <template v-slot="scope">
                      <div class="google-data-page__table-metric">
                        <span>{{
                          formatNumber(scope.row.search_impressions)
                        }}</span>
                        <span
                          :class="
                            momClass(
                              scope.row.mom && scope.row.mom.search_impressions
                            )
                          "
                        >
                          {{
                            formatMom(
                              scope.row.mom && scope.row.mom.search_impressions
                            )
                          }}
                        </span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="CTR" width="125">
                    <template v-slot="scope">
                      <div class="google-data-page__table-metric">
                        <span>{{ scope.row.search_ctr }}</span>
                        <span :class="momClass(scope.row.search_ctr_mom)">
                          {{ formatPointMom(scope.row.search_ctr_mom) }}
                        </span>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="最近同步" width="150">
                    <template v-slot="scope">
                      {{ formatTime(scope.row.last_sync_at) }}
                    </template>
                  </el-table-column>
                  <el-table-column label="状态" width="110">
                    <template v-slot="scope">
                      <status-badge
                        :status="scope.row.last_sync_status"
                      ></status-badge>
                    </template>
                  </el-table-column>
                  <el-table-column label="操作" width="120" fixed="right">
                    <template v-slot="scope">
                      <el-button type="text" @click="openSiteDetail(scope.row)">
                        详情
                      </el-button>
                      <el-button
                        v-if="$hasPermission('google_data', 'sync')"
                        type="text"
                        :loading="syncingSiteId === scope.row.site_id"
                        :disabled="
                          !scope.row.ga4_enabled && !scope.row.gsc_enabled
                        "
                        @click="syncSite(scope.row)"
                      >
                        同步
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <div class="google-data-page__pagination">
                <el-pagination
                  background
                  layout="total, sizes, prev, pager, next, jumper"
                  :current-page="sitePager.page"
                  :page-size="sitePager.size"
                  :page-sizes="[10, 20, 50]"
                  :total="sitePager.count"
                  @size-change="handleSiteSizeChange"
                  @current-change="handleSitePageChange"
                ></el-pagination>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'site-detail'">
        <div v-loading="siteDetailLoading">
          <div class="google-data-page__detail-actions">
            <el-button
              size="small"
              icon="el-icon-arrow-left"
              @click="backToSiteData"
            >
              返回站点数据
            </el-button>
          </div>
          <div class="google-data-page__detail-title">
            {{ siteDetail.site_name || "站点详情" }}
            <span v-if="siteDetail.site_url">/ {{ siteDetail.site_url }}</span>
          </div>

          <div class="google-data-page__panel google-data-page__info-panel">
            <div class="google-data-page__info-item">
              <span>Google 账号</span>
              <strong>{{ siteDetail.google_email || "-" }}</strong>
            </div>
            <div class="google-data-page__info-item">
              <span>GA4 Property ID</span>
              <strong>{{ siteDetail.ga4_property_id || "-" }}</strong>
            </div>
            <div class="google-data-page__info-item">
              <span>GSC Site URL</span>
              <strong>{{ siteDetail.gsc_site_url || "-" }}</strong>
            </div>
            <div class="google-data-page__info-item">
              <span>最近同步时间</span>
              <strong>{{ formatTime(siteDetail.last_sync_at) }}</strong>
            </div>
            <div class="google-data-page__info-item">
              <span>同步状态</span>
              <status-badge
                :status="siteDetail.last_sync_status"
              ></status-badge>
            </div>
          </div>

          <div class="google-data-page__stats google-data-page__stats--detail">
            <stat-card
              label="GA4 访问数"
              :value="formatNumber(siteDetail.sessions)"
              :extra="formatMom(siteDetail.mom && siteDetail.mom.sessions)"
              :extra-class="momClass(siteDetail.mom && siteDetail.mom.sessions)"
              icon="el-icon-data-line"
              tone="blue"
            />
            <stat-card
              label="GA4 访客数"
              :value="formatNumber(siteDetail.active_users)"
              :extra="formatMom(siteDetail.mom && siteDetail.mom.active_users)"
              :extra-class="
                momClass(siteDetail.mom && siteDetail.mom.active_users)
              "
              icon="el-icon-user"
              tone="green"
            />
            <stat-card
              label="GSC 点击数"
              :value="formatNumber(siteDetail.search_clicks)"
              :extra="formatMom(siteDetail.mom && siteDetail.mom.search_clicks)"
              :extra-class="
                momClass(siteDetail.mom && siteDetail.mom.search_clicks)
              "
              icon="el-icon-position"
              tone="purple"
            />
            <stat-card
              label="GSC 曝光数"
              :value="formatNumber(siteDetail.search_impressions)"
              :extra="
                formatMom(siteDetail.mom && siteDetail.mom.search_impressions)
              "
              :extra-class="
                momClass(siteDetail.mom && siteDetail.mom.search_impressions)
              "
              icon="el-icon-view"
              tone="orange"
            />
            <stat-card
              label="平均点击率 CTR"
              :value="siteDetail.search_ctr || '0%'"
              :extra="formatPointMom(siteDetail.search_ctr_mom)"
              :extra-class="momClass(siteDetail.search_ctr_mom)"
              icon="el-icon-pie-chart"
              tone="green"
            />
            <stat-card
              label="询盘线索数"
              :value="siteDetail.clue_count || 0"
              :extra="formatMom(siteDetail.clue_mom)"
              :extra-class="momClass(siteDetail.clue_mom)"
              icon="el-icon-message"
              tone="red"
            />
          </div>

          <div class="google-data-page__panel google-data-page__trend-panel">
            <div class="google-data-page__panel-head">
              <div class="google-data-page__panel-title">
                趋势图（多指标对比）
              </div>
              <span class="google-data-page__panel-note">
                {{ currentRangeLabel }}
              </span>
            </div>
            <google-trend-chart :rows="siteTrendRows" :height="270" />
          </div>

          <div class="google-data-page__country-grid">
            <div class="google-data-page__panel google-data-page__trend-panel">
              <div class="google-data-page__panel-head">
                <div class="google-data-page__panel-title">国家/地区热力图</div>
                <span class="google-data-page__panel-note">
                  {{ currentRangeLabel }} / GA4 访问数
                </span>
              </div>
              <google-country-map
                v-loading="countryLoading"
                :rows="topCountryRows"
                :height="380"
              />
            </div>

            <div class="google-data-page__panel">
              <div class="google-data-page__panel-title">国家/地区排名</div>
              <el-table
                v-loading="countryLoading"
                :data="topCountryRows"
                size="small"
                empty-text="暂无国家地区数据"
              >
                <el-table-column label="排名" width="60">
                  <template v-slot="scope">{{ scope.$index + 1 }}</template>
                </el-table-column>
                <el-table-column
                  prop="country"
                  label="国家/地区"
                  min-width="120"
                />
                <el-table-column prop="sessions" label="访问数" width="90" />
                <el-table-column
                  prop="active_users"
                  label="访客数"
                  width="90"
                />
              </el-table>
            </div>
          </div>

          <div
            class="google-data-page__detail-grid google-data-page__detail-grid--bottom"
          >
            <div class="google-data-page__panel">
              <div class="google-data-page__panel-title">
                Top 页面（Landing Page）
              </div>
              <el-table
                v-loading="topPagesLoading"
                :data="siteTopPages"
                size="small"
                empty-text="暂无页面数据"
              >
                <el-table-column label="序号" width="58">
                  <template v-slot="scope">{{ scope.$index + 1 }}</template>
                </el-table-column>
                <el-table-column
                  prop="page_url"
                  label="页面"
                  min-width="210"
                  show-overflow-tooltip
                />
                <el-table-column prop="search_clicks" label="点击" width="78" />
                <el-table-column
                  prop="search_impressions"
                  label="曝光"
                  width="86"
                />
                <el-table-column prop="search_ctr" label="CTR" width="76" />
                <el-table-column
                  prop="search_position"
                  label="平均排名"
                  width="76"
                />
              </el-table>
            </div>
            <div class="google-data-page__panel">
              <div class="google-data-page__panel-title">
                Top 搜索词（Queries）
              </div>
              <el-table
                v-loading="topQueriesLoading"
                :data="siteTopQueries"
                size="small"
                empty-text="暂无搜索词数据"
              >
                <el-table-column label="序号" width="58">
                  <template v-slot="scope">{{ scope.$index + 1 }}</template>
                </el-table-column>
                <el-table-column
                  prop="query_text"
                  label="搜索词"
                  min-width="170"
                  show-overflow-tooltip
                />
                <el-table-column prop="search_clicks" label="点击" width="78" />
                <el-table-column
                  prop="search_impressions"
                  label="曝光"
                  width="86"
                />
                <el-table-column prop="search_ctr" label="CTR" width="76" />
                <el-table-column
                  prop="search_position"
                  label="平均排名"
                  width="76"
                />
              </el-table>
            </div>
            <div class="google-data-page__panel">
              <div class="google-data-page__panel-title">同步历史</div>
              <el-table
                :data="siteDetailLogs"
                size="small"
                empty-text="暂无同步历史"
              >
                <el-table-column prop="provider" label="类型" width="70" />
                <el-table-column prop="status" label="状态" width="90">
                  <template v-slot="scope">
                    <status-badge :status="scope.row.status"></status-badge>
                  </template>
                </el-table-column>
                <el-table-column prop="stat_month" label="月份" width="90" />
                <el-table-column label="时间" width="135">
                  <template v-slot="scope">{{
                    formatTime(scope.row.create_time)
                  }}</template>
                </el-table-column>
                <el-table-column
                  prop="result_text"
                  label="结果"
                  min-width="160"
                  show-overflow-tooltip
                />
              </el-table>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activeTab === 'accounts'" class="google-data-page__panel">
        <div class="google-data-page__toolbar">
          <el-button
            v-if="$hasPermission('google_data', 'manage_account')"
            type="primary"
            size="small"
            @click="openAccountDialog()"
          >
            新增账号
          </el-button>
        </div>
        <el-table
          v-loading="accountsLoading"
          :data="accountPager.list"
          empty-text="暂无授权账号"
        >
          <el-table-column prop="email" label="Google账号" min-width="200" />
          <el-table-column label="状态" width="100">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="scope.row.status ? 'success' : 'info'"
              >
                {{ scope.row.status ? "正常" : "停用" }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最近授权" width="160">
            <template v-slot="scope">
              {{ formatTime(scope.row.last_auth_at) }}
            </template>
          </el-table-column>
          <el-table-column label="授权状态" width="100">
            <template v-slot="scope">
              <el-tag
                size="small"
                :type="scope.row.authorized ? 'success' : 'warning'"
              >
                {{ authText(scope.row) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="最近检测" width="160">
            <template v-slot="scope">
              {{ formatTime(scope.row.update_time) }}
            </template>
          </el-table-column>
          <el-table-column
            prop="last_error"
            label="最近错误"
            min-width="220"
            show-overflow-tooltip
          />
          <el-table-column label="操作" width="200" fixed="right">
            <template v-slot="scope">
              <el-button
                v-if="$hasPermission('google_data', 'manage_account')"
                type="text"
                :loading="checkingAccountId === scope.row.id"
                :disabled="
                  !scope.row.authorized ||
                  !scope.row.status ||
                  scope.row.auth_type === 'service_account'
                "
                @click="checkAccount(scope.row)"
              >
                检测
              </el-button>
              <el-button
                v-if="$hasPermission('google_data', 'manage_account')"
                type="text"
                :loading="authorizingAccountId === scope.row.id"
                @click="authorizeAccount(scope.row)"
              >
                {{ scope.row.authorized ? "重新授权" : "授权" }}
              </el-button>
              <el-button
                v-if="$hasPermission('google_data', 'manage_account')"
                type="text"
                @click="openAccountDialog(scope.row)"
              >
                编辑
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section v-if="activeTab === 'bindings'" class="google-data-page__panel">
        <div class="google-data-page__toolbar">
          <el-button
            v-if="$hasPermission('google_data', 'manage_binding')"
            type="primary"
            size="small"
            @click="openBindingDialog()"
          >
            新增绑定
          </el-button>
        </div>
        <el-table
          v-loading="bindingsLoading"
          :data="bindingPager.list"
          empty-text="暂无站点绑定"
        >
          <el-table-column
            prop="site_name"
            label="站点"
            min-width="160"
            show-overflow-tooltip
          />
          <el-table-column
            prop="google_email"
            label="Google账号"
            min-width="180"
            show-overflow-tooltip
          />
          <el-table-column
            prop="ga4_property_id"
            label="GA4 Property"
            min-width="140"
            show-overflow-tooltip
          />
          <el-table-column
            prop="gsc_site_url"
            label="GSC Site URL"
            min-width="220"
            show-overflow-tooltip
          />
          <el-table-column label="最近同步" width="160">
            <template v-slot="scope">
              {{ formatTime(scope.row.last_sync_at) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="190" fixed="right">
            <template v-slot="scope">
              <el-button
                v-if="$hasPermission('google_data', 'sync')"
                type="text"
                :loading="syncingSiteId === scope.row.site_id"
                @click="syncSite(scope.row)"
              >
                同步
              </el-button>
              <el-button
                v-if="$hasPermission('google_data', 'sync')"
                type="text"
                :loading="checkingBindingId === scope.row.site_id"
                @click="checkBinding(scope.row)"
              >
                检测
              </el-button>
              <el-button
                v-if="$hasPermission('google_data', 'manage_binding')"
                type="text"
                @click="openBindingDialog(scope.row)"
              >
                编辑
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>

      <section v-if="activeTab === 'logs'" class="google-data-page__panel">
        <div class="google-data-page__filters google-data-page__toolbar">
          <el-select
            v-model="logParams.provider"
            clearable
            size="small"
            placeholder="全部来源"
            class="google-data-page__log-select"
            @change="refreshLogs"
          >
            <el-option
              v-for="item in logOptions.providers"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
          <el-select
            v-model="logParams.log_action"
            clearable
            filterable
            size="small"
            placeholder="全部操作"
            class="google-data-page__log-action"
            @change="refreshLogs"
          >
            <el-option
              v-for="item in logOptions.actions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            ></el-option>
          </el-select>
          <el-select
            v-model="logParams.status"
            clearable
            size="small"
            placeholder="全部状态"
            class="google-data-page__log-select"
            @change="refreshLogs"
          >
            <el-option label="成功" value="success"></el-option>
            <el-option label="失败" value="failed"></el-option>
            <el-option label="部分失败" value="partial_failed"></el-option>
            <el-option label="执行中" value="running"></el-option>
          </el-select>
          <el-select
            v-model="logParams.site_id"
            clearable
            filterable
            size="small"
            placeholder="全部站点"
            class="google-data-page__site-select"
            @change="refreshLogs"
          >
            <el-option
              v-for="item in siteOptions"
              :key="item.id"
              :label="`${item.name}（${item.url}）`"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-select
            v-model="logParams.google_account_id"
            clearable
            filterable
            size="small"
            placeholder="全部Google账号"
            class="google-data-page__account-select"
            @change="refreshLogs"
          >
            <el-option
              v-for="item in logOptions.accounts"
              :key="item.id"
              :label="item.email"
              :value="item.id"
            ></el-option>
          </el-select>
          <el-date-picker
            v-model="logParams.month"
            type="month"
            value-format="yyyy-MM"
            size="small"
            placeholder="全部月份"
            class="google-data-page__month-picker"
            @change="refreshLogs"
          ></el-date-picker>
          <el-button
            size="small"
            type="primary"
            icon="el-icon-search"
            @click="refreshLogs"
          >
            查询
          </el-button>
          <el-button size="small" @click="resetLogFilters">重置</el-button>
        </div>
        <div class="data-table-scroll" style="--table-min-width: 1340px">
          <el-table
            v-loading="logsLoading"
            :data="logPager.list"
            empty-text="暂无同步日志"
          >
            <el-table-column label="来源" width="90">
              <template v-slot="scope">
                {{ formatLogProvider(scope.row.provider) }}
              </template>
            </el-table-column>
            <el-table-column
              prop="site_name"
              label="站点"
              min-width="150"
              show-overflow-tooltip
            />
            <el-table-column
              prop="google_email"
              label="Google账号"
              min-width="180"
              show-overflow-tooltip
            />
            <el-table-column prop="stat_month" label="月份" width="100" />
            <el-table-column label="操作" width="150">
              <template v-slot="scope">
                {{ formatLogAction(scope.row.action) }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="110">
              <template v-slot="scope">
                <status-badge :status="scope.row.status"></status-badge>
              </template>
            </el-table-column>
            <el-table-column
              label="请求结果"
              min-width="180"
              show-overflow-tooltip
            >
              <template v-slot="scope">
                {{ scope.row.result_text || scope.row.error_message || "-" }}
              </template>
            </el-table-column>
            <el-table-column
              prop="error_message"
              label="错误详情"
              min-width="220"
              show-overflow-tooltip
            />
            <el-table-column label="时间" width="160">
              <template v-slot="scope">
                {{ formatTime(scope.row.create_time) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="google-data-page__pagination">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="logPager.page"
            :page-size="logPager.size"
            :page-sizes="[10, 20, 50]"
            :total="logPager.count"
            @size-change="handleLogSizeChange"
            @current-change="handleLogPageChange"
          ></el-pagination>
        </div>
      </section>

      <section v-if="activeTab === 'settings'" class="google-data-page__panel">
        <el-form
          ref="settingsFormRef"
          v-loading="settingsLoading"
          :model="settingsForm"
          :rules="settingsRules"
          label-width="180px"
          size="small"
        >
          <el-form-item label="OAuth Client ID" prop="client_id">
            <el-input
              v-model="settingsForm.client_id"
              class="google-data-page__setting-input"
              placeholder="请输入 Google OAuth Client ID"
            ></el-input>
          </el-form-item>
          <el-form-item label="OAuth Client Secret" prop="client_secret">
            <el-input
              v-model="settingsForm.client_secret"
              class="google-data-page__setting-input"
              type="password"
              show-password
              placeholder="留空则不修改已保存密钥"
            ></el-input>
            <div class="google-data-page__tips">
              当前状态：{{
                settingsForm.client_secret_configured ? "已配置" : "未配置"
              }}<template v-if="settingsForm.client_secret_suffix">
                ，后4位：{{ settingsForm.client_secret_suffix }} </template
              >；环境变量：{{
                settingsForm.env_configured ? "已配置" : "未配置"
              }}。Client Secret 加密保存，不回显明文。
            </div>
          </el-form-item>
          <el-form-item label="Redirect URI" prop="redirect_uri">
            <el-input
              v-model="settingsForm.redirect_uri"
              class="google-data-page__setting-input"
              placeholder="http://127.0.0.1:8088/admapi/googleData/oauthCallback"
            ></el-input>
          </el-form-item>
          <el-form-item label="授权完成跳转地址" prop="frontend_url">
            <el-input
              v-model="settingsForm.frontend_url"
              class="google-data-page__setting-input"
              placeholder="http://127.0.0.1:8088/manage-admin/#/google/accounts"
            ></el-input>
          </el-form-item>
          <el-form-item label="默认 GSC 资源">
            <el-radio-group v-model="settingsForm.default_gsc_site_mode">
              <el-radio-button label="domain"
                >域名资源 sc-domain</el-radio-button
              >
              <el-radio-button label="url_prefix">URL 前缀</el-radio-button>
            </el-radio-group>
            <div class="google-data-page__tips">
              当前建议使用域名资源，例如 sc-domain:czrunwo.com。
            </div>
          </el-form-item>
          <el-form-item label="默认同步开关">
            <el-checkbox
              v-model="settingsForm.ga4_enabled_default"
              :true-label="1"
              :false-label="0"
            >
              新增绑定默认开启 GA4
            </el-checkbox>
            <el-checkbox
              v-model="settingsForm.gsc_enabled_default"
              :true-label="1"
              :false-label="0"
            >
              新增绑定默认开启 GSC
            </el-checkbox>
          </el-form-item>
          <el-form-item>
            <el-button
              v-if="$hasPermission('google_data', 'manage_setting')"
              type="primary"
              :loading="settingsSaving"
              @click="saveSettings"
            >
              保存设置
            </el-button>
            <el-button
              v-if="$hasPermission('google_data', 'manage_setting')"
              :loading="settingsTesting"
              @click="testSettings"
            >
              检测配置
            </el-button>
          </el-form-item>
        </el-form>

        <div v-loading="syncStatusLoading" class="google-data-page__cron-panel">
          <div class="google-data-page__panel-head">
            <div>
              <div class="google-data-page__panel-title">定时同步任务</div>
              <div class="google-data-page__cron-subtitle">
                建议每天自动同步最近2个月，避免月初漏掉上月末数据。
              </div>
            </div>
            <el-button
              size="mini"
              icon="el-icon-refresh"
              :loading="syncStatusLoading"
              @click="getSyncStatus(true)"
            >
              检测状态
            </el-button>
          </div>

          <el-alert
            :title="syncStatus.message || '暂无同步状态'"
            :type="syncStatusType"
            show-icon
            :closable="false"
          ></el-alert>

          <div class="google-data-page__cron-grid">
            <div>
              <div class="google-data-page__cron-label">Crontab 状态</div>
              <div class="google-data-page__cron-value">
                <el-tag
                  size="small"
                  :type="cronInstalledTagType(syncStatus.cron_installed)"
                >
                  {{ syncStatus.cron_install_status_text || "未检测" }}
                </el-tag>
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">当前实际同步频率</div>
              <div class="google-data-page__cron-value">
                {{
                  syncStatus.cron_installed === null
                    ? "检测受限"
                    : syncStatus.actual_frequency || "未安装"
                }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">最近同步时间</div>
              <div class="google-data-page__cron-value">
                {{ formatTime(syncStatus.last_sync_at) }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">距离最近同步</div>
              <div class="google-data-page__cron-value">
                {{ formatSyncAge(syncStatus.last_sync_age_seconds) }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">启用绑定站点</div>
              <div class="google-data-page__cron-value">
                {{ syncStatus.bound_site_count || 0 }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">当前异常站点</div>
              <div class="google-data-page__cron-value">
                {{ syncStatus.failed_site_count || 0 }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">下次预计执行</div>
              <div class="google-data-page__cron-value">
                {{ syncStatus.next_run_hint || "-" }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">状态检测时间</div>
              <div class="google-data-page__cron-value">
                {{ formatTime(syncStatus.checked_at) }}
              </div>
            </div>
            <div>
              <div class="google-data-page__cron-label">日志更新时间</div>
              <div class="google-data-page__cron-value">
                {{
                  formatTime(
                    syncStatus.cron_log && syncStatus.cron_log.updated_at
                  )
                }}
              </div>
            </div>
          </div>

          <div class="google-data-page__cron-command">
            <div class="google-data-page__cron-label">当前已安装 crontab</div>
            <pre>{{
              syncStatus.cron_installed_line ||
              syncStatus.cron_check_error ||
              "未检测到 Google 数据同步 crontab"
            }}</pre>
          </div>
          <div class="google-data-page__cron-command">
            <div class="google-data-page__cron-label">推荐 crontab</div>
            <pre>{{ syncStatus.recommended_cron || "-" }}</pre>
          </div>
          <div class="google-data-page__cron-command">
            <div class="google-data-page__cron-label">
              执行日志：{{
                (syncStatus.cron_log && syncStatus.cron_log.path) || "-"
              }}
            </div>
            <pre>{{
              (syncStatus.cron_log && syncStatus.cron_log.tail) ||
              (syncStatus.cron_log && syncStatus.cron_log.message) ||
              "-"
            }}</pre>
          </div>
          <div
            v-if="
              syncStatus.recent_failed_log && syncStatus.recent_failed_log.id
            "
            class="google-data-page__cron-failed"
          >
            最近失败：{{
              syncStatus.recent_failed_log.site_name || "未知站点"
            }}
            / {{ formatLogProvider(syncStatus.recent_failed_log.provider) }} /
            {{ formatLogAction(syncStatus.recent_failed_log.action) }} /
            {{ syncStatus.recent_failed_log.error_message || "-" }}
          </div>
        </div>
      </section>
    </div>

    <el-dialog
      :title="accountForm.id ? '编辑Google账号' : '新增Google账号'"
      :visible.sync="accountDialogVisible"
      width="520px"
    >
      <el-form
        ref="accountFormRef"
        :model="accountForm"
        :rules="accountRules"
        label-width="110px"
        size="small"
      >
        <el-form-item label="Google邮箱" prop="email">
          <el-input
            v-model="accountForm.email"
            placeholder="请输入Google账号邮箱"
          ></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="accountForm.status"
            :active-value="1"
            :inactive-value="0"
            active-text="正常"
            inactive-text="停用"
          ></el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="accountDialogVisible = false">
          取消
        </el-button>
        <el-button
          size="small"
          type="primary"
          :loading="accountSaving"
          @click="saveAccount"
        >
          保存
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="bindingForm.id ? '编辑站点绑定' : '新增站点绑定'"
      :visible.sync="bindingDialogVisible"
      width="620px"
    >
      <el-form
        ref="bindingFormRef"
        :model="bindingForm"
        :rules="bindingRules"
        label-width="130px"
        size="small"
      >
        <el-form-item label="系统站点" prop="site_id">
          <el-select
            v-model="bindingForm.site_id"
            filterable
            placeholder="请选择系统站点"
            style="width: 100%"
          >
            <el-option
              v-for="item in siteOptions"
              :key="item.id"
              :label="`${item.name}（${item.url}）`"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Google账号" prop="google_account_id">
          <el-select
            v-model="bindingForm.google_account_id"
            filterable
            placeholder="请选择Google账号"
            style="width: 100%"
          >
            <el-option
              v-for="item in accountPager.list"
              :key="item.id"
              :label="item.email"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="GA4 Property ID" prop="ga4_property_id">
          <el-input
            v-model="bindingForm.ga4_property_id"
            placeholder="例如：123456789"
          ></el-input>
        </el-form-item>
        <el-form-item label="GSC Site URL" prop="gsc_site_url">
          <el-input
            v-model="bindingForm.gsc_site_url"
            placeholder="例如：sc-domain:example.com"
          ></el-input>
          <div class="google-data-page__tips">
            域名资源填写 sc-domain:域名；URL 前缀资源才填写完整 https:// 地址。
          </div>
        </el-form-item>
        <el-form-item label="同步开关">
          <el-checkbox
            v-model="bindingForm.ga4_enabled"
            :true-label="1"
            :false-label="0"
          >
            GA4同步
          </el-checkbox>
          <el-checkbox
            v-model="bindingForm.gsc_enabled"
            :true-label="1"
            :false-label="0"
          >
            GSC同步
          </el-checkbox>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-switch
            v-model="bindingForm.status"
            :active-value="1"
            :inactive-value="0"
            active-text="正常"
            inactive-text="停用"
          ></el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="bindingDialogVisible = false">
          取消
        </el-button>
        <el-button
          size="small"
          type="primary"
          :loading="bindingSaving"
          @click="saveBinding"
        >
          保存
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  apiGoogleAccounts,
  apiGoogleCheckAccount,
  apiGoogleCheckBinding,
  apiGoogleCountryStats,
  apiGoogleDailyTrend,
  apiGoogleOAuthUrl,
  apiGoogleOverview,
  apiGoogleSaveAccount,
  apiGoogleSaveBinding,
  apiGoogleSaveSettings,
  apiGoogleSettings,
  apiGoogleSiteBindings,
  apiGoogleSiteData,
  apiGoogleSyncSite,
  apiGoogleSyncStatus,
  apiGoogleSyncLogs,
  apiGoogleTestSettings,
  apiGoogleTopPages,
  apiGoogleTopQueries,
} from "@/api/google";
import { formatDate } from "@/utils/util";
import GoogleCountryMap from "./components/GoogleCountryMap.vue";
import GoogleTrendChart from "./components/GoogleTrendChart.vue";

const StatCard = {
  props: {
    label: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      default: 0,
    },
    icon: {
      type: String,
      default: "el-icon-data-analysis",
    },
    tone: {
      type: String,
      default: "blue",
    },
    extra: {
      type: String,
      default: "",
    },
    extraClass: {
      type: [String, Array, Object],
      default: "",
    },
  },
  render(h) {
    return h("div", { class: "google-data-page__stat-card" }, [
      h("div", { class: "google-data-page__stat-glow" }),
      h(
        "div",
        {
          class: [
            "google-data-page__stat-icon",
            `google-data-page__stat-icon--${this.tone}`,
          ],
        },
        [h("i", { class: this.icon })]
      ),
      h("div", { class: "google-data-page__stat-main" }, [
        h("div", { class: "google-data-page__stat-label" }, this.label),
        h("div", { class: "google-data-page__stat-value" }, String(this.value)),
        this.extra
          ? h(
              "div",
              {
                class: ["google-data-page__stat-extra", this.extraClass],
              },
              this.extra
            )
          : null,
      ]),
    ]);
  },
};

const StatusBadge = {
  props: {
    status: {
      type: String,
      default: "",
    },
  },
  computed: {
    label() {
      const map = {
        success: "同步成功",
        failed: "同步失败",
        partial_failed: "部分失败",
        pending: "待同步",
        running: "同步中",
      };
      return map[this.status] || this.status || "待同步";
    },
    type() {
      if (this.status === "success") return "success";
      if (this.status === "failed" || this.status === "partial_failed")
        return "danger";
      if (this.status === "running") return "primary";
      return "warning";
    },
  },
  render(h) {
    return h(
      "el-tag",
      { props: { size: "small", type: this.type } },
      this.label
    );
  },
};

const pager = () => ({
  page: 1,
  size: 10,
  count: 0,
  list: [],
});

const accountFormDefault = () => ({
  id: "",
  email: "",
  status: 1,
});

const bindingFormDefault = () => ({
  id: "",
  site_id: "",
  google_account_id: "",
  ga4_property_id: "",
  gsc_site_url: "",
  ga4_enabled: 1,
  gsc_enabled: 1,
  status: 1,
});

const settingsFormDefault = () => ({
  client_id: "",
  client_secret: "",
  client_secret_configured: 0,
  redirect_uri: "",
  frontend_url: "",
  default_gsc_site_mode: "domain",
  ga4_enabled_default: 1,
  gsc_enabled_default: 1,
  env_configured: 0,
  config_configured: 0,
  source: "",
});

const currentMonth = () => formatDate(new Date(), "yyyy-mm");

export default {
  components: {
    GoogleCountryMap,
    GoogleTrendChart,
    StatCard,
    StatusBadge,
  },
  data() {
    return {
      activeTab: "overview",
      month: currentMonth(),
      rangeParams: {
        time_range: "30d",
        custom_time: [],
      },
      timeRangeOptions: [
        { label: "今日", value: "today" },
        { label: "近7天", value: "7d" },
        { label: "近30天", value: "30d" },
        { label: "近半年", value: "half_year" },
        { label: "近一年", value: "1y" },
        { label: "自定义", value: "custom" },
      ],
      overviewLoading: false,
      siteDataLoading: false,
      accountsLoading: false,
      bindingsLoading: false,
      countryLoading: false,
      topPagesLoading: false,
      topQueriesLoading: false,
      logsLoading: false,
      settingsLoading: false,
      syncStatusLoading: false,
      overviewSiteLoading: false,
      overviewQueriesLoading: false,
      siteDetailLoading: false,
      overview: {},
      overviewSites: [],
      overviewTopQueries: [],
      overviewTrendRows: [],
      siteSummary: {},
      siteDetail: {},
      siteDetailLogs: [],
      siteTrendRows: [],
      siteTopPages: [],
      siteTopQueries: [],
      siteOptions: [],
      countryRows: [],
      syncStatus: {},
      siteParams: {
        site_id: "",
        keyword: "",
      },
      logParams: {
        provider: "",
        log_action: "",
        status: "",
        site_id: "",
        google_account_id: "",
        month: "",
      },
      sitePager: pager(),
      accountPager: pager(),
      bindingPager: pager(),
      logPager: pager(),
      logOptions: {
        providers: [],
        actions: [],
        accounts: [],
      },
      accountDialogVisible: false,
      bindingDialogVisible: false,
      accountSaving: false,
      bindingSaving: false,
      settingsSaving: false,
      settingsTesting: false,
      authorizingAccountId: "",
      checkingAccountId: "",
      syncingSiteId: "",
      checkingBindingId: "",
      accountForm: accountFormDefault(),
      bindingForm: bindingFormDefault(),
      settingsForm: settingsFormDefault(),
      accountRules: {
        email: [
          { required: true, trigger: "blur", message: "请输入Google账号邮箱" },
          { type: "email", trigger: "blur", message: "请输入正确邮箱" },
        ],
      },
      bindingRules: {
        site_id: [{ required: true, trigger: "change", message: "请选择站点" }],
        google_account_id: [
          { required: true, trigger: "change", message: "请选择Google账号" },
        ],
        ga4_property_id: [
          {
            pattern: /^\d*$/,
            trigger: "blur",
            message: "GA4 Property ID 必须是纯数字",
          },
        ],
      },
      settingsRules: {
        client_id: [
          {
            required: true,
            trigger: "blur",
            message: "请输入 OAuth Client ID",
          },
        ],
        redirect_uri: [
          { required: true, trigger: "blur", message: "请输入 Redirect URI" },
          { type: "url", trigger: "blur", message: "Redirect URI 格式不正确" },
        ],
        frontend_url: [
          { required: true, trigger: "blur", message: "请输入后台跳转地址" },
          { type: "url", trigger: "blur", message: "后台跳转地址格式不正确" },
        ],
      },
    };
  },
  computed: {
    showRangePicker() {
      return ["overview", "site-data", "site-detail"].includes(this.activeTab);
    },
    showMonthPicker() {
      return ["logs"].includes(this.activeTab);
    },
    currentRangeLabel() {
      if (
        this.rangeParams.time_range === "custom" &&
        this.rangeParams.custom_time &&
        this.rangeParams.custom_time.length === 2
      ) {
        return `${this.rangeParams.custom_time[0]} 至 ${this.rangeParams.custom_time[1]}`;
      }
      const option = this.timeRangeOptions.find(
        (item) => item.value === this.rangeParams.time_range
      );
      return option ? option.label : "近30天";
    },
    pageTitle() {
      const map = {
        overview: "数据看板",
        "site-data": "站点数据",
        "site-detail": "站点详情",
        accounts: "授权账号",
        bindings: "站点绑定",
        logs: "同步日志",
        settings: "Google 设置",
      };
      return map[this.activeTab] || "Google 数据";
    },
    pageDesc() {
      const map = {
        overview: "查看全部已绑定站点的 GA4 / GSC 汇总、同步状态和异常提醒。",
        "site-data": "按站点横向比较访问、点击、曝光和同步状态。",
        "site-detail":
          "查看单个站点的绑定信息、月度指标、国家地区排行和同步历史。",
        accounts: "管理 Google 授权账号和 OAuth 授权状态。",
        bindings: "维护系统站点与 Google 账号、GA4、GSC 的绑定关系。",
        logs: "查看 GA4 / GSC 同步过程和失败原因。",
        settings: "维护 Google OAuth 配置和默认同步开关。",
      };
      return (
        map[this.activeTab] ||
        "管理 GA4 / Search Console 授权、站点绑定、同步状态和站点数据。"
      );
    },
    overviewFailedLogs() {
      return this.overview.failed_sites || [];
    },
    topCountryRows() {
      return (this.countryRows || []).slice(0, 10);
    },
    syncStatusType() {
      const map = {
        success: "success",
        warning: "warning",
        failed: "error",
        pending: "warning",
      };
      return map[this.syncStatus.status] || "info";
    },
    cronInstalledTagType() {
      return (value) => {
        if (value === 1) return "success";
        if (value === 0) return "danger";
        return "warning";
      };
    },
  },
  created() {
    this.setActiveTabFromRoute();
    this.handleOAuthResult();
    this.refreshCurrentTab();
  },
  watch: {
    "$route.path"() {
      this.setActiveTabFromRoute();
      this.refreshCurrentTab();
    },
  },
  methods: {
    setActiveTabFromRoute() {
      this.activeTab = this.$route.meta?.googleTab || "overview";
    },
    handleOAuthResult() {
      const query = this.$route.query || {};
      if (query.google_oauth === "success") {
        this.$message.success("Google账号授权成功");
        this.activeTab = "accounts";
        this.getAccounts();
        this.$router.replace({ path: "/google/accounts", query: {} });
      } else if (query.google_oauth === "failed") {
        this.$message.error(query.message || "Google账号授权失败");
        this.activeTab = "accounts";
        this.getAccounts();
        this.$router.replace({ path: "/google/accounts", query: {} });
      }
    },
    refreshCurrentTab() {
      const actions = {
        overview: this.getOverview,
        "site-data": this.getSiteData,
        "site-detail": this.getSiteDetail,
        accounts: this.getAccounts,
        bindings: this.getBindings,
        logs: this.getLogs,
        settings: this.getSettings,
      };
      if (actions[this.activeTab]) {
        actions[this.activeTab]();
      }
    },
    handleRangeChange(value) {
      if (value !== "custom") {
        this.rangeParams.custom_time = [];
        this.refreshCurrentTab();
      }
    },
    buildRangeParams() {
      const params = {
        time_range: this.rangeParams.time_range || "30d",
      };
      if (
        params.time_range === "custom" &&
        this.rangeParams.custom_time &&
        this.rangeParams.custom_time.length === 2
      ) {
        params.start_time = this.rangeParams.custom_time[0];
        params.end_time = this.rangeParams.custom_time[1];
      }
      return params;
    },
    getOverview() {
      this.overviewLoading = true;
      apiGoogleOverview({ month: this.month, ...this.buildRangeParams() })
        .then((resp) => {
          this.overview = resp || {};
          this.getOverviewSites();
          this.getOverviewTrend();
          this.getOverviewTopQueries();
          this.getLogs();
        })
        .finally(() => {
          this.overviewLoading = false;
        });
    },
    getOverviewTrend() {
      apiGoogleDailyTrend({
        month: this.month,
        ...this.buildRangeParams(),
      }).then((resp) => {
        this.overviewTrendRows = resp.list || [];
      });
    },
    getOverviewSites() {
      this.overviewSiteLoading = true;
      apiGoogleSiteData({
        month: this.month,
        ...this.buildRangeParams(),
        page: 1,
        size: 5,
      })
        .then((resp) => {
          this.overviewSites = (resp.list || [])
            .slice()
            .sort((a, b) => Number(b.sessions || 0) - Number(a.sessions || 0))
            .slice(0, 5);
          this.siteOptions = resp.sites || this.siteOptions;
        })
        .finally(() => {
          this.overviewSiteLoading = false;
        });
    },
    getOverviewTopQueries() {
      this.overviewQueriesLoading = true;
      apiGoogleTopQueries({
        month: this.month,
        ...this.buildRangeParams(),
        limit: 10,
      })
        .then((resp) => {
          this.overviewTopQueries = resp.list || [];
        })
        .finally(() => {
          this.overviewQueriesLoading = false;
        });
    },
    getSiteData() {
      this.siteDataLoading = true;
      this.getOverview();
      this.getLogs();
      apiGoogleSiteData({
        ...this.siteParams,
        month: this.month,
        ...this.buildRangeParams(),
        page: this.sitePager.page,
        size: this.sitePager.size,
      })
        .then((resp) => {
          this.sitePager = {
            ...this.sitePager,
            list: resp.list || [],
            count: resp.count || 0,
          };
          this.siteSummary = resp.summary || {};
          this.siteOptions = resp.sites || [];
        })
        .finally(() => {
          this.siteDataLoading = false;
        });
    },
    getCountryStats(siteId) {
      this.countryLoading = true;
      apiGoogleCountryStats({
        month: this.month,
        ...this.buildRangeParams(),
        site_id: siteId || this.siteParams.site_id,
      })
        .then((resp) => {
          this.countryRows = resp.list || [];
          this.siteOptions = resp.sites || this.siteOptions;
        })
        .finally(() => {
          this.countryLoading = false;
        });
    },
    getSiteDetail() {
      const siteId = Number(this.$route.params.siteId || 0);
      if (!siteId) {
        this.backToSiteData();
        return;
      }
      this.siteDetailLoading = true;
      this.countryRows = [];
      this.siteTrendRows = [];
      this.siteTopPages = [];
      this.siteTopQueries = [];
      apiGoogleSiteData({
        site_id: siteId,
        month: this.month,
        ...this.buildRangeParams(),
        page: 1,
        size: 1,
      })
        .then((resp) => {
          const row = (resp.list || [])[0] || {};
          this.siteDetail = { ...row };
          this.siteOptions = resp.sites || this.siteOptions;
          this.getCountryStats(siteId);
          this.getSiteTrend(siteId);
          this.getTopPages(siteId);
          this.getTopQueries(siteId);
          return apiGoogleSiteBindings({ site_id: siteId, page: 1, size: 1 });
        })
        .then((resp) => {
          const binding = (resp.list || [])[0] || {};
          this.siteDetail = { ...this.siteDetail, ...binding };
          return apiGoogleSyncLogs({ site_id: siteId, page: 1, size: 5 });
        })
        .then((resp) => {
          this.siteDetailLogs = resp.list || [];
        })
        .finally(() => {
          this.siteDetailLoading = false;
        });
    },
    getSiteTrend(siteId) {
      if (!siteId) return;
      apiGoogleDailyTrend({
        month: this.month,
        site_id: siteId,
        ...this.buildRangeParams(),
      }).then((resp) => {
        this.siteTrendRows = resp.list || [];
      });
    },
    getTopPages(siteId) {
      if (!siteId) return;
      this.topPagesLoading = true;
      apiGoogleTopPages({
        month: this.month,
        ...this.buildRangeParams(),
        site_id: siteId,
        limit: 10,
      })
        .then((resp) => {
          this.siteTopPages = resp.list || [];
        })
        .finally(() => {
          this.topPagesLoading = false;
        });
    },
    getTopQueries(siteId) {
      if (!siteId) return;
      this.topQueriesLoading = true;
      apiGoogleTopQueries({
        month: this.month,
        ...this.buildRangeParams(),
        site_id: siteId,
        limit: 10,
      })
        .then((resp) => {
          this.siteTopQueries = resp.list || [];
        })
        .finally(() => {
          this.topQueriesLoading = false;
        });
    },
    getAccounts() {
      this.accountsLoading = true;
      apiGoogleAccounts({
        page: this.accountPager.page,
        size: this.accountPager.size,
      })
        .then((resp) => {
          this.accountPager = { ...this.accountPager, ...resp };
        })
        .finally(() => {
          this.accountsLoading = false;
        });
    },
    getBindings() {
      this.bindingsLoading = true;
      if (!this.accountPager.list.length) {
        this.getAccounts();
      }
      apiGoogleSiteBindings({
        page: this.bindingPager.page,
        size: this.bindingPager.size,
      })
        .then((resp) => {
          this.bindingPager = { ...this.bindingPager, ...resp };
          this.siteOptions = resp.sites || this.siteOptions;
        })
        .finally(() => {
          this.bindingsLoading = false;
        });
    },
    getLogs() {
      this.logsLoading = true;
      apiGoogleSyncLogs({
        ...this.logParams,
        page: this.logPager.page,
        size: this.logPager.size,
      })
        .then((resp) => {
          this.logPager = { ...this.logPager, ...resp };
          this.siteOptions = resp.sites || this.siteOptions;
          this.logOptions = {
            providers: resp.providers || this.logOptions.providers,
            actions: resp.actions || this.logOptions.actions,
            accounts: resp.accounts || this.logOptions.accounts,
          };
        })
        .finally(() => {
          this.logsLoading = false;
        });
    },
    getSettings() {
      this.settingsLoading = true;
      this.getSyncStatus();
      apiGoogleSettings()
        .then((resp) => {
          this.settingsForm = {
            ...settingsFormDefault(),
            ...(resp || {}),
            client_secret: "",
          };
        })
        .finally(() => {
          this.settingsLoading = false;
        });
    },
    getSyncStatus(showMessage = false) {
      this.syncStatusLoading = true;
      apiGoogleSyncStatus()
        .then((resp) => {
          this.syncStatus = resp || {};
          if (showMessage) {
            this.$message.success("定时同步任务状态已刷新");
          }
        })
        .finally(() => {
          this.syncStatusLoading = false;
        });
    },
    saveSettings() {
      this.$refs.settingsFormRef.validate((valid) => {
        if (!valid) return;
        this.settingsSaving = true;
        apiGoogleSaveSettings(this.settingsForm)
          .then(() => {
            this.$message.success("保存成功");
            this.getSettings();
          })
          .finally(() => {
            this.settingsSaving = false;
          });
      });
    },
    testSettings() {
      this.settingsTesting = true;
      apiGoogleTestSettings()
        .then((resp) => {
          this.$message.success(resp.message || "Google OAuth 配置完整");
        })
        .finally(() => {
          this.settingsTesting = false;
        });
    },
    formatTime(value) {
      return value ? formatDate(value, "yyyy-mm-dd hh:MM") : "-";
    },
    formatNumber(value) {
      const number = Number(value || 0);
      return number.toLocaleString();
    },
    formatMom(item) {
      if (!item) return "";
      const change = Number(item.change || 0);
      const rate = item.rate;
      if (change === 0) {
        return `- 0${rate === null ? "" : " / 0%"}`;
      }
      const arrow = change > 0 ? "↑" : "↓";
      const sign = change > 0 ? "+" : "-";
      const value = Math.abs(change).toLocaleString();
      const rateText = rate === null ? "" : ` / ${sign}${Math.abs(rate)}%`;
      return `${arrow} ${sign}${value}${rateText}`;
    },
    formatPointMom(item) {
      if (!item) return "";
      const change = Number(item.change || 0);
      const rate = item.rate;
      if (change === 0) {
        return `- 0pp${rate === null ? "" : " / 0%"}`;
      }
      const arrow = change > 0 ? "↑" : "↓";
      const sign = change > 0 ? "+" : "-";
      const rateText = rate === null ? "" : ` / ${sign}${Math.abs(rate)}%`;
      return `${arrow} ${sign}${Math.abs(change)}pp${rateText}`;
    },
    momClass(item) {
      if (!item) return "google-data-page__mom";
      const change = Number(item.change || 0);
      return [
        "google-data-page__mom",
        change > 0
          ? "google-data-page__mom--up"
          : change < 0
          ? "google-data-page__mom--down"
          : "google-data-page__mom--flat",
      ];
    },
    formatSyncAge(seconds) {
      if (seconds === null || seconds === undefined) return "-";
      const value = Number(seconds || 0);
      if (value < 60) return "刚刚";
      if (value < 3600) return `${Math.floor(value / 60)}分钟前`;
      if (value < 86400) return `${Math.floor(value / 3600)}小时前`;
      return `${Math.floor(value / 86400)}天前`;
    },
    formatLogProvider(provider) {
      const map = {
        ga4: "GA4",
        gsc: "GSC",
        google: "Google",
        oauth: "OAuth",
        config: "Google设置",
      };
      return map[provider] || provider || "-";
    },
    formatLogAction(action) {
      const map = {
        google_setting_update: "保存Google设置",
        google_setting_check: "检测Google设置",
        google_account_auth: "授权Google账号",
        google_account_health_check: "检测授权账号",
        google_site_sync_manual: "手动同步站点",
        google_site_binding_check: "检测站点绑定",
        run_report_summary: "GA4汇总",
        run_report_daily: "GA4日趋势",
        run_report_country: "GA4国家分布",
        run_report_country_daily: "GA4日级国家分布",
        search_analytics_summary: "GSC汇总",
        search_analytics_daily: "GSC日趋势",
        search_analytics_pages: "GSC Top页面",
        search_analytics_queries: "GSC搜索词",
        test_ga4_property: "检测GA4",
        test_gsc_site: "检测GSC",
        sync_preflight: "同步预检",
      };
      return map[action] || action || "-";
    },
    syncText(row) {
      if (!row.ga4_enabled && !row.gsc_enabled) {
        return "未绑定";
      }
      return row.last_sync_status || "待同步";
    },
    authText(row) {
      if (row.auth_type === "service_account") {
        return "已废弃";
      }
      return row.authorized ? "已授权" : "未授权";
    },
    openAccountDialog(row) {
      this.accountForm = {
        ...accountFormDefault(),
        ...(row || {}),
      };
      this.accountDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.accountFormRef && this.$refs.accountFormRef.clearValidate();
      });
    },
    saveAccount() {
      this.$refs.accountFormRef.validate((valid) => {
        if (!valid) return;
        this.accountSaving = true;
        apiGoogleSaveAccount(this.accountForm)
          .then(() => {
            this.$message.success("保存成功");
            this.accountDialogVisible = false;
            this.getAccounts();
          })
          .finally(() => {
            this.accountSaving = false;
          });
      });
    },
    authorizeAccount(row) {
      this.authorizingAccountId = row.id;
      apiGoogleOAuthUrl({ account_id: row.id })
        .then((resp) => {
          if (resp && resp.auth_url) {
            window.location.href = resp.auth_url;
          }
        })
        .finally(() => {
          this.authorizingAccountId = "";
        });
    },
    checkAccount(row) {
      this.checkingAccountId = row.id;
      apiGoogleCheckAccount({ account_id: row.id })
        .then((resp) => {
          this.$message.success(resp.message || "授权有效");
          this.getAccounts();
        })
        .finally(() => {
          this.checkingAccountId = "";
        });
    },
    syncSite(row) {
      const siteId = row.site_id;
      this.$confirm(`确定同步 ${this.month} 的Google数据吗？`, "确认同步", {
        type: "warning",
      })
        .then(() => {
          this.syncingSiteId = siteId;
          return apiGoogleSyncSite({
            site_id: siteId,
            month: this.month,
          });
        })
        .then((resp) => {
          const message = `同步完成：成功${resp.success || 0}，失败${
            resp.failed || 0
          }，GA4 ${resp.ga4_success || 0}，GSC ${resp.gsc_success || 0}`;
          if (Number(resp.failed || 0) > 0) {
            this.$message.warning(`${message}。请查看同步日志。`);
          } else {
            this.$message.success(message);
          }
          this.refreshAfterSync(siteId);
        })
        .finally(() => {
          this.syncingSiteId = "";
        });
    },
    syncSelectedSite() {
      const site = (this.sitePager.list || []).find(
        (item) => Number(item.site_id) === Number(this.siteParams.site_id)
      );
      if (site) {
        this.syncSite(site);
      }
    },
    checkBinding(row) {
      const siteId = row.site_id;
      this.checkingBindingId = siteId;
      apiGoogleCheckBinding({ site_id: siteId })
        .then((resp) => {
          const ga4 = resp.ga4 || {};
          const gsc = resp.gsc || {};
          const type = resp.status === "success" ? "success" : "warning";
          this.$message({
            type,
            message: `检测完成：GA4 ${ga4.message || "-"}；GSC ${
              gsc.message || "-"
            }`,
            duration: 6000,
          });
          this.getLogs();
        })
        .finally(() => {
          this.checkingBindingId = "";
        });
    },
    openSiteDetail(row) {
      this.$router.push(`/google/data/detail/${row.site_id}`);
    },
    refreshAfterSync(siteId) {
      this.getOverview();
      this.getLogs();
      if (this.activeTab === "site-detail") {
        this.getSiteDetail();
        return;
      }
      if (this.activeTab === "bindings") {
        this.getBindings();
        return;
      }
      this.getSiteData();
      if (siteId) {
        this.getCountryStats(siteId);
      }
    },
    refreshLogs() {
      this.logPager.page = 1;
      this.getLogs();
    },
    resetLogFilters() {
      this.logParams = {
        provider: "",
        log_action: "",
        status: "",
        site_id: "",
        google_account_id: "",
        month: "",
      };
      this.logPager.page = 1;
      this.getLogs();
    },
    handleLogPageChange(page) {
      this.logPager.page = page;
      this.getLogs();
    },
    handleLogSizeChange(size) {
      this.logPager.size = size;
      this.logPager.page = 1;
      this.getLogs();
    },
    backToSiteData() {
      this.$router.push("/google/data");
    },
    resetSiteFilters() {
      this.siteParams = {
        site_id: "",
        keyword: "",
      };
      this.sitePager.page = 1;
      this.getSiteData();
    },
    handleSitePageChange(page) {
      this.sitePager.page = page;
      this.getSiteData();
    },
    handleSiteSizeChange(size) {
      this.sitePager.size = size;
      this.sitePager.page = 1;
      this.getSiteData();
    },
    openBindingDialog(row) {
      if (!this.accountPager.list.length) {
        this.getAccounts();
      }
      if (!this.siteOptions.length) {
        this.getSiteData();
      }
      this.bindingForm = {
        ...bindingFormDefault(),
        ga4_enabled: this.settingsForm.ga4_enabled_default,
        gsc_enabled: this.settingsForm.gsc_enabled_default,
        ...(row || {}),
      };
      this.bindingDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.bindingFormRef && this.$refs.bindingFormRef.clearValidate();
      });
    },
    saveBinding() {
      this.$refs.bindingFormRef.validate((valid) => {
        if (!valid) return;
        this.bindingSaving = true;
        apiGoogleSaveBinding(this.bindingForm)
          .then(() => {
            this.$message.success("保存成功");
            this.bindingDialogVisible = false;
            this.getBindings();
            this.getOverview();
          })
          .finally(() => {
            this.bindingSaving = false;
          });
      });
    },
  },
};
</script>

<style lang="scss">
.google-data-page {
  padding: 2px 0 18px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 20px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__desc {
    margin-top: 6px;
    font-size: 13px;
    color: #909399;
  }

  &__header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  &__range {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;

    .el-radio-button__inner {
      min-width: 66px;
      padding: 9px 14px;
    }
  }

  &__content {
    min-height: 320px;
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  &__stats--wide {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    margin-bottom: 12px;
  }

  &__stats--site {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 14px 0;
  }

  &__stats--detail {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    margin: 14px 0;
  }

  &__stat-card {
    position: relative;
    overflow: hidden;
    min-height: 132px;
    border: 1px solid rgba(223, 230, 240, 0.92);
    border-radius: 8px;
    padding: 18px;
    background: #fff;
    box-shadow: 0 10px 24px rgba(34, 50, 74, 0.06);
    display: flex;
    align-items: center;
    gap: 14px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 16px 30px rgba(34, 50, 74, 0.1);
    }
  }

  &__stat-glow {
    position: absolute;
    right: -38px;
    top: -38px;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    opacity: 0.14;
    background: #2f7cf6;
    pointer-events: none;
  }

  &__stat-icon {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    position: relative;
    z-index: 1;
    color: #fff;
    box-shadow: 0 10px 18px rgba(47, 124, 246, 0.28);

    i {
      font-size: 21px;
    }
  }

  &__stat-icon--blue {
    background: linear-gradient(135deg, #2f7cf6, #12c2e9);
  }

  &__stat-icon--green {
    background: linear-gradient(135deg, #18c48f, #5ddebd);
    box-shadow: 0 10px 18px rgba(24, 196, 143, 0.28);
  }

  &__stat-icon--purple {
    background: linear-gradient(135deg, #7c5cff, #a874ff);
    box-shadow: 0 10px 18px rgba(124, 92, 255, 0.3);
  }

  &__stat-icon--orange {
    background: linear-gradient(135deg, #ff9f1a, #ffc247);
    box-shadow: 0 10px 18px rgba(255, 159, 26, 0.3);
  }

  &__stat-icon--red {
    background: linear-gradient(135deg, #ff4d57, #ff8a65);
    box-shadow: 0 10px 18px rgba(255, 77, 87, 0.26);
  }

  &__stat-icon--gray {
    background: linear-gradient(135deg, #607089, #9aa8ba);
    box-shadow: 0 10px 18px rgba(96, 112, 137, 0.24);
  }

  &__stat-main {
    position: relative;
    z-index: 1;
    min-width: 0;
  }

  &__stat-label {
    font-size: 13px;
    color: #607089;
    white-space: nowrap;
  }

  &__stat-value {
    margin-top: 8px;
    font-size: 32px;
    font-weight: 700;
    line-height: 40px;
    color: #18233c;
    white-space: nowrap;
  }

  &__stat-extra {
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
  }

  &__freshness {
    margin: 0 0 14px;
    font-size: 12px;
    color: #909399;
  }

  &__overview-queries {
    margin-bottom: 14px;
  }

  &__mom {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.35;
    white-space: nowrap;
  }

  &__mom--up {
    color: #18a058;
    background: #eaf8f0;
  }

  &__mom--down {
    color: #f56c6c;
    background: #fff0f0;
  }

  &__mom--flat {
    color: #909399;
    background: #f4f6f8;
  }

  &__table-metric {
    display: flex;
    min-height: 42px;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    line-height: 1.2;

    .google-data-page__mom {
      padding: 2px 6px;
      font-size: 11px;
    }
  }

  &__site-cell {
    min-width: 0;
    line-height: 1.45;
  }

  &__site-name,
  &__site-url {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__site-name {
    color: #303133;
    font-weight: 500;
  }

  &__site-url {
    margin-top: 2px;
    color: #909399;
    font-size: 12px;
  }

  &__dashboard-grid,
  &__detail-grid {
    display: grid;
    gap: 14px;
  }

  &__dashboard-grid {
    grid-template-columns: minmax(0, 3fr) minmax(360px, 1.55fr);
    margin-bottom: 14px;
  }

  &__detail-grid {
    grid-template-columns: minmax(0, 1.15fr) minmax(360px, 0.85fr);
  }

  &__detail-grid--bottom {
    margin-top: 14px;
  }

  &__country-grid {
    display: grid;
    grid-template-columns: minmax(0, 7fr) minmax(320px, 3fr);
    gap: 14px;
    margin-top: 14px;
  }

  &__panel {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 16px;
    background: #fff;
    box-shadow: 0 8px 20px rgba(31, 45, 61, 0.035);
  }

  &__panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  &__panel-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__panel-note {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
  }

  &__filter-panel {
    padding: 14px 16px;
  }

  &__filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__toolbar {
    margin-bottom: 12px;
  }

  &__sub-title {
    margin: 18px 0 10px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
  }

  &__site-select {
    width: 240px;
  }

  &__account-select {
    width: 250px;
  }

  &__log-select {
    width: 130px;
  }

  &__log-action {
    width: 180px;
  }

  &__month-picker {
    width: 135px;
  }

  &__keyword {
    width: 260px;
  }

  &__setting-input {
    width: 560px;
    max-width: 100%;
  }

  &__tips {
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.6;
    color: #909399;
  }

  &__cron-panel {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
  }

  &__cron-subtitle {
    margin-top: 5px;
    font-size: 12px;
    color: #909399;
  }

  &__cron-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(120px, 1fr));
    gap: 12px;
    margin: 14px 0;
  }

  &__cron-label {
    font-size: 12px;
    color: #909399;
  }

  &__cron-value {
    margin-top: 5px;
    font-size: 16px;
    font-weight: 600;
    color: #1f2d3d;
  }

  &__cron-command {
    margin-top: 12px;

    pre {
      margin: 8px 0 0;
      padding: 12px;
      overflow: auto;
      border-radius: 6px;
      background: #f5f7fa;
      color: #303133;
      font-size: 12px;
      line-height: 1.6;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }

  &__cron-failed {
    margin-top: 10px;
    color: #f56c6c;
    font-size: 12px;
    line-height: 1.6;
  }

  &__simple-chart {
    position: relative;
    height: 230px;
    border-radius: 8px;
    overflow: hidden;
    background: linear-gradient(
          to bottom,
          rgba(144, 147, 153, 0.18) 1px,
          transparent 1px
        )
        0 24px / 100% 46px,
      linear-gradient(180deg, #fbfdff 0%, #f6f9ff 100%);
  }

  &__simple-chart--detail {
    height: 270px;
  }

  &__simple-chart-line {
    position: absolute;
    left: 34px;
    right: 34px;
    height: 3px;
    border-radius: 999px;
    opacity: 0.92;
  }

  &__simple-chart-line--blue {
    top: 84px;
    background: linear-gradient(90deg, #1677ff 0%, #409eff 45%, #1677ff 100%);
    transform: translateY(0) skewY(-4deg);
  }

  &__simple-chart-line--green {
    top: 124px;
    background: linear-gradient(90deg, #13c2c2 0%, #36cfc9 50%, #13c2c2 100%);
    transform: translateY(0) skewY(3deg);
  }

  &__simple-chart-line--orange {
    top: 136px;
    background: repeating-linear-gradient(
      90deg,
      #f59a23 0,
      #f59a23 10px,
      transparent 10px,
      transparent 16px
    );
    transform: translateY(0) skewY(5deg);
  }

  &__simple-chart-empty {
    position: absolute;
    left: 24px;
    right: 24px;
    bottom: 18px;
    color: #909399;
    font-size: 13px;
    text-align: center;
  }

  &__pagination {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  }

  &__detail-actions {
    margin-bottom: 12px;
  }

  &__detail-title {
    margin-bottom: 14px;
    font-size: 20px;
    font-weight: 600;
    color: #1f2d3d;

    span {
      font-weight: 500;
      color: #607089;
    }
  }

  &__info-panel {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
  }

  &__info-item {
    min-width: 0;
    border-right: 1px solid #ebeef5;
    padding-right: 12px;

    &:last-child {
      border-right: 0;
    }

    span {
      display: block;
      margin-bottom: 8px;
      color: #607089;
      font-size: 13px;
    }

    strong {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #1f2d3d;
      font-size: 14px;
      font-weight: 600;
    }
  }
}

@media (max-width: 1360px) {
  .google-data-page {
    &__stats--wide,
    &__stats--detail {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    &__dashboard-grid,
    &__detail-grid,
    &__country-grid {
      grid-template-columns: 1fr;
    }

    &__info-panel {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}
</style>
